import { useEffect, useMemo, useRef, useState } from 'react'
import { Box } from '@mui/material'
import { useWatch } from 'react-hook-form'
import { LoadConfirmationFormValues } from '../schema'
import { mapFormToDocument } from '../utils/map-form-to-document'
import { paginateDocument } from '../utils/paginate-document'
import { LoadConfirmationDocument } from '../template/LoadConfirmationDocument'
import '../template/load-confirmation-template.css'

const A4_WIDTH_PX = 794

export type LoadConfirmationPreviewProps = {
  loadNumber: string
  logoUrl?: string
}

export function LoadConfirmationPreview({
  loadNumber,
  logoUrl,
}: LoadConfirmationPreviewProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(0.55)
  const formValues = useWatch() as LoadConfirmationFormValues

  const { data, pages } = useMemo(() => {
    const doc = mapFormToDocument(formValues ?? {}, loadNumber)
    return { data: doc, pages: paginateDocument(doc) }
  }, [formValues, loadNumber])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const updateScale = () => {
      const el = containerRef.current
      if (!el) return
      const width = el.clientWidth
      if (width > 0) {
        setScale(Math.min(width / A4_WIDTH_PX, 1))
      }
    }

    updateScale()
    const observer = new ResizeObserver(updateScale)
    observer.observe(container)
    return () => observer.disconnect()
  }, [])

  return (
    <Box
      ref={containerRef}
      sx={{
        height: '100%',
        overflow: 'auto',
        bgcolor: '#f0f0f0',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
      }}
    >
      <Box
        sx={{
          transform: `scale(${scale})`,
          transformOrigin: 'top center',
          width: A4_WIDTH_PX,
          flexShrink: 0,
        }}
      >
        <LoadConfirmationDocument data={data} pages={pages} logoUrl={logoUrl} />
      </Box>
    </Box>
  )
}
